/* eslint-disable */
import React from 'react';
interface PaginationProps {
  currentPage: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

const getPageNumbers = (current: number, total: number, max = 5): number[] => {
  if (total <= max) return Array.from({ length: total }, (_, i) => i + 1);
  const half = Math.floor(max / 2);
  let start = Math.max(1, current - half);
  let end = Math.min(total, current + half);
  if (current <= half) {
    end = max;
  } else if (current + half >= total) {
    start = total - max + 1;
  }
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalCount,
  pageSize,
  onPageChange,
}) => {
  const totalPages = Math.ceil(totalCount / pageSize);
  if (totalPages <= 1) return null;
  const pageNumbers = getPageNumbers(currentPage, totalPages);
  const showStartEllipsis = pageNumbers[0] > 1;
  const showEndEllipsis = pageNumbers[pageNumbers.length - 1] < totalPages;

  return (
    <nav className="flex items-center gap-2 select-none">
      <button
        type="button"
        className="px-3 py-1 text-base rounded border text-gray-500 hover:bg-gray-100 disabled:opacity-50"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Previous Page"
      >
        &lt;
      </button>
      {showStartEllipsis && (
        <>
          <button
            type="button"
            key={1}
            className={`px-4 py-2 text-base rounded font-medium border transition-colors ${
              currentPage === 1
                ? 'bg-blue-600 text-white border-blue-600'
                : 'text-gray-700 border-gray-200 hover:bg-blue-50'
            }`}
            onClick={() => onPageChange(1)}
            aria-current={currentPage === 1 ? 'page' : undefined}
          >
            1
          </button>
          <span key="start-ellipsis" className="px-2 text-base text-gray-400">
            ...
          </span>
        </>
      )}
      {pageNumbers.map(num => (
        <button
          type="button"
          key={`page-${num}`}
          className={`px-4 py-2 text-base rounded font-medium border transition-colors ${
            num === currentPage
              ? 'bg-blue-600 text-white border-blue-600'
              : 'text-gray-700 border-gray-200 hover:bg-blue-50'
          }`}
          onClick={() => onPageChange(num)}
          aria-current={num === currentPage ? 'page' : undefined}
        >
          {num}
        </button>
      ))}
      {showEndEllipsis && (
        <>
          <span key="end-ellipsis" className="px-2 text-base text-gray-400">
            ...
          </span>
          <button
            type="button"
            key={totalPages}
            className={`px-4 py-2 text-base rounded font-medium border transition-colors ${
              currentPage === totalPages
                ? 'bg-blue-600 text-white border-blue-600'
                : 'text-gray-700 border-gray-200 hover:bg-blue-50'
            }`}
            onClick={() => onPageChange(totalPages)}
            aria-current={currentPage === totalPages ? 'page' : undefined}
          >
            {totalPages}
          </button>
        </>
      )}
      <button
        type="button"
        className="px-3 py-1 text-base rounded border text-gray-500 hover:bg-gray-100 disabled:opacity-50"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Next Page"
      >
        &gt;
      </button>
    </nav>
  );
};

export default Pagination;
